import { Component, OnInit, OnDestroy, HostListener, ElementRef, inject, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SavedReport, ReportData, getReportRouteParams } from '../../shared/helpers/report-search.utils';
import { ReportSearchService } from '../../shared/services/report-search.service';
import { CommonHttpService } from '../../shared/services/common-http.service';
import { CommonService } from '../../shared/services/common.service';
import { AuthenticationService } from '../../shared/services/authenticate.service';
import { NavigationEnd, Router } from '@angular/router';
import { Tourservice } from '../../shared/services/tourservice';
@Component({
  selector: 'bi-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  standalone:false
})
export class HeaderComponent implements OnInit, OnDestroy {
  reportsData: ReportData = { modules: [] };
  filteredItems: SavedReport[] = [];
  searchKeyword: string = '';
  selectedItem: any;
  showSearchResults: boolean = false;

  jobs: any[] = [];
  jobsSummary: { pending: number; processing: number; completed: number; failed: number; cancelled: number } = { pending: 0, processing: 0, completed: 0, failed: 0, cancelled: 0 };
  jobsLoading = false;
  showJobsDialog = false;
  selectedStatusFilter: string = '';
  jobsProgressRate: { [jobId: string]: { rate: number; eta: string } } = {};
  showCancelConfirm = false;
  jobToCancel: any = null;
  cancellingJobId: string | null = null;
  exportToastDismissed = false;
  exportToastSessionId = 0;
  readonly exportToastUiEnabled = true;
  private exportToastActivated = false;
  private exportToastAwaitUserDismiss = false;
  private exportSessionJobIds = new Set<string>();

  private jobsStreamAbort: AbortController | null = null;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private prevSnapshot: { [jobId: string]: { processed: number; at: number } } = {};
  private isFetching = false;
  private usingPollingFallback = false;
  private exportJobQueuedSub: Subscription | null = null;
  private authSub: Subscription | null = null;
  private routerSub: Subscription | null = null;
  private myJobsFetchedAfterAuth = false;
  private readonly POLL_INTERVAL_MS = 3000;

  readonly failedJobUserMessage = 'This export could not be completed. Please try again.';

  getFailedJobMessage(): string {
    return this.failedJobUserMessage;
  }

  private sanitizeJob(job: any): any {
    const { error, ...safeJob } = job;
    return safeJob;
  }

  get filteredJobs(): any[] {
    if (!this.selectedStatusFilter) return this.jobs;
    return this.jobs.filter(j => j.jobStatus === this.selectedStatusFilter);
  }

  get hasActiveDownloads(): boolean {
    return (this.jobsSummary.pending + this.jobsSummary.processing) > 0;
  }

  get headerDownloadProgress(): number {
    return this.exportToastOverallProgress;
  }

  get exportToastActiveCount(): number {
    if (!this.exportToastActivated) {
      return 0;
    }

    return this.jobs.filter(job =>
      this.exportSessionJobIds.has(job.jobId) &&
      (job.jobStatus === 'pending' || job.jobStatus === 'processing')
    ).length;
  }

  get exportToastCompletedCount(): number {
    if (!this.exportToastActivated) {
      return 0;
    }

    return this.jobs.filter(job =>
      this.exportSessionJobIds.has(job.jobId) &&
      job.jobStatus === 'completed' &&
      job.result?.success === 1
    ).length;
  }

  get exportToastOverallProgress(): number {
    if (!this.exportToastActivated) {
      return 0;
    }

    const sessionJobs = this.jobs.filter(job => this.exportSessionJobIds.has(job.jobId));
    const activeJobs = sessionJobs.filter(job => job.jobStatus === 'pending' || job.jobStatus === 'processing');
    const completedJobs = sessionJobs.filter(job =>
      job.jobStatus === 'completed' &&
      job.result?.success === 1
    );

    if (activeJobs.length > 0) {
      const progressSum = activeJobs.reduce((sum, job) => sum + (job.progress ?? 0), 0);
      return Math.min(100, Math.round(progressSum / activeJobs.length));
    }

    return completedJobs.length > 0 ? 100 : 0;
  }

  get exportToastProcessedRecords(): number {
    if (!this.exportToastActivated) {
      return 0;
    }

    const sessionJobs = this.jobs.filter(job => this.exportSessionJobIds.has(job.jobId));

    if (this.exportToastAwaitUserDismiss && !this.hasActiveDownloads) {
      return sessionJobs
        .filter(job => job.jobStatus === 'completed' && job.result?.success === 1)
        .reduce((sum, job) => sum + (job.totalRecords ?? job.processedRecords ?? 0), 0);
    }

    return sessionJobs
      .filter(job => job.jobStatus === 'pending' || job.jobStatus === 'processing')
      .reduce((sum, job) => sum + (job.processedRecords ?? 0), 0);
  }

  get exportToastTotalRecords(): number {
    if (!this.exportToastActivated) {
      return 0;
    }

    const sessionJobs = this.jobs.filter(job => this.exportSessionJobIds.has(job.jobId));
    const activeJobs = sessionJobs.filter(job => job.jobStatus === 'pending' || job.jobStatus === 'processing');

    if (activeJobs.length === 0 && this.exportToastAwaitUserDismiss) {
      return sessionJobs
        .filter(job => job.jobStatus === 'completed' && job.result?.success === 1)
        .reduce((sum, job) => sum + (job.totalRecords ?? 0), 0);
    }

    return activeJobs.reduce((sum, job) => sum + (job.totalRecords ?? 0), 0);
  }

  private trackExportSessionJobs(jobs: any[]): void {
    if (!this.exportToastActivated) {
      return;
    }

    jobs.forEach(job => {
      if (['pending', 'processing', 'completed'].includes(job.jobStatus)) {
        this.exportSessionJobIds.add(job.jobId);
      }
    });
  }

  get headerDownloadStatusText(): string {
    const activeCount = this.jobsSummary.pending + this.jobsSummary.processing;
    if (activeCount === 0) return '';

    if (this.jobsSummary.processing > 0) {
      return activeCount === 1
        ? `Exporting ${this.headerDownloadProgress}%`
        : `${activeCount} exports in progress`;
    }

    return activeCount === 1 ? 'Export queued' : `${activeCount} exports queued`;
  }

  get exportToastAwaitingDismiss(): boolean {
    return this.exportToastAwaitUserDismiss;
  }

  get showExportToast(): boolean {
    return this.exportToastUiEnabled && this.exportToastActivated && !this.exportToastDismissed;
  }

  get exportToastReady(): boolean {
    return this.exportToastAwaitUserDismiss && !this.hasActiveDownloads;
  }

  get latestCompletedDownloadUrl(): string {
    const completedJob = this.jobs.find(job =>
      job.jobStatus === 'completed' &&
      job.result?.success === 1 &&
      job.result?.reportUrl
    );

    return completedJob?.result?.reportUrl ?? '';
  }

  onExportToastDismissed(): void {
    this.exportToastDismissed = true;
    this.exportToastAwaitUserDismiss = false;
    this.exportToastActivated = false;
    this.exportSessionJobIds.clear();
  }

  private activateExportToast(startNewSession: boolean): void {
    this.exportToastActivated = true;
    this.exportToastDismissed = false;
    this.exportToastAwaitUserDismiss = false;

    if (startNewSession) {
      this.exportToastSessionId++;
      this.exportSessionJobIds.clear();
    }

    this.syncJobsMonitoring();
    this.fetchJobs(false);
  }

  filterByStatus(status: string) {
    this.selectedStatusFilter = this.selectedStatusFilter === status ? '' : status;
  }

  onDialogHide() {
    this.showCancelConfirm = false;
    this.jobToCancel = null;
    this.syncJobsMonitoring();
  }

  openCancelConfirm(job: any) {
    this.jobToCancel = job;
    this.showCancelConfirm = true;
  }

  confirmCancelJob() {
    if (!this.jobToCancel) return;
    const jobId = this.jobToCancel.jobId;
    this.cancellingJobId = jobId;
    this.showCancelConfirm = false;

    this._commonHttpService.cancelJob(jobId).subscribe({
      next: () => {
        // Optimistically update the job status in the list
        this.jobs = this.jobs.map(j =>
          j.jobId === jobId ? { ...j, jobStatus: 'cancelled', progress: j.progress } : j
        );
        this.jobsSummary.cancelled = (this.jobsSummary.cancelled ?? 0) + 1;
        if (this.jobToCancel?.jobStatus === 'pending') this.jobsSummary.pending = Math.max(0, this.jobsSummary.pending - 1);
        if (this.jobToCancel?.jobStatus === 'processing') this.jobsSummary.processing = Math.max(0, this.jobsSummary.processing - 1);
        this.cancellingJobId = null;
        this.jobToCancel = null;
        this.syncJobsMonitoring();
      },
      error: () => {
        this.cancellingJobId = null;
        this.jobToCancel = null;
      }
    });
  }

  ngOnDestroy() {
    this.exportJobQueuedSub?.unsubscribe();
    this.authSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.clearJobsStream();
  }

  private isAuthenticated(): boolean {
    return !!this._authenticationService.getBearerToken();
  }

  private isOnAuthorizationRoute(): boolean {
    return this.router.url.includes('/authorization') ||
      (typeof window !== 'undefined' && window.location.hash.includes('/authorization'));
  }

  isOnReportDetailsRoute(): boolean {
    return this.router.url.includes('/report-details');
  }

  private canFetchMyJobs(): boolean {
    return this._authenticationService.isAuthTokenReady && this.isAuthenticated();
  }

  private onAuthTokenReady(): void {
    if (!this.canFetchMyJobs()) {
      this.myJobsFetchedAfterAuth = false;
      return;
    }

    this.fetchMyJobsOnceAfterAuth();
    this.syncJobsMonitoring();
  }

  private fetchMyJobsOnceAfterAuth(): void {
    if (this.myJobsFetchedAfterAuth || !this.canFetchMyJobs()) {
      return;
    }

    this.myJobsFetchedAfterAuth = true;
    this.fetchJobs(false);
  }

  private syncJobsForCurrentRoute(): void {
    if (!this.canFetchMyJobs()) {
      this.clearJobsStream();
      return;
    }

    this.syncJobsMonitoring();
  }

  private shouldMonitorJobs(): boolean {
    return this.showJobsDialog || this.hasActiveDownloads;
  }

  private syncJobsMonitoring(showSpinner = false) {
    if (this.shouldMonitorJobs()) {
      if (!this.jobsStreamAbort && !this.usingPollingFallback) {
        this.startJobsStream(showSpinner && this.showJobsDialog);
      }
      return;
    }

    this.clearJobsStream();
  }

  private applyJobsResponse(res: any) {
    const newJobs: any[] = (res?.data ?? []).map((job: any) => this.sanitizeJob(job));
    this.jobsSummary = res?.summary ?? { pending: 0, processing: 0, completed: 0, failed: 0, cancelled: 0 };
    const now = Date.now();

    newJobs.forEach(job => {
      const prev = this.prevSnapshot[job.jobId];
      if (prev && job.jobStatus === 'processing') {
        const delta = job.processedRecords - prev.processed;
        const elapsedSec = Math.max(0.001, (now - prev.at) / 1000);
        const ratePerSec = Math.max(0, Math.round(delta / elapsedSec));
        const remaining = job.totalRecords - job.processedRecords;
        const etaSec = ratePerSec > 0 ? remaining / ratePerSec : null;
        this.jobsProgressRate[job.jobId] = {
          rate: ratePerSec,
          eta: etaSec !== null ? this.formatEta(etaSec) : '--'
        };
      }
      this.prevSnapshot[job.jobId] = { processed: job.processedRecords, at: now };
    });

    this.jobs = newJobs;
    this.jobsLoading = false;
    this.isFetching = false;

    if (this.hasActiveDownloads) {
      this.exportToastDismissed = false;
      this.exportToastActivated = true;
    }

    this.trackExportSessionJobs(newJobs);
    this.updateExportToastCompletionState();
    this.syncJobsMonitoring();
  }

  private updateExportToastCompletionState(): void {
    if (!this.exportToastActivated || this.exportToastDismissed) {
      return;
    }

    const sessionJobs = this.jobs.filter(job => this.exportSessionJobIds.has(job.jobId));
    const hasSessionActive = sessionJobs.some(job =>
      job.jobStatus === 'pending' || job.jobStatus === 'processing'
    );
    const hasSessionSuccess = sessionJobs.some(job =>
      job.jobStatus === 'completed' && job.result?.success === 1
    );

    if (!hasSessionActive && hasSessionSuccess) {
      this.exportToastAwaitUserDismiss = true;
    }
  }

  /**
   * Fallback HTTP fetch used when SSE is unavailable.
   */
  private fetchJobs(showSpinner = false) {
    if (!this.canFetchMyJobs() || this.isFetching) return;
    this.isFetching = true;
    if (showSpinner) this.jobsLoading = true;

    this._commonHttpService.getMyJobs().subscribe({
      next: (res: any) => this.ngZone.run(() => this.applyJobsResponse(res)),
      error: () => this.ngZone.run(() => {
        this.jobsLoading = false;
        this.isFetching = false;
      })
    });
  }

  private startJobsStream(showSpinner = false) {
    if (!this.canFetchMyJobs() || this.jobsStreamAbort || this.usingPollingFallback) return;

    if (showSpinner) {
      this.jobsLoading = true;
    }

    this.jobsStreamAbort = this._commonHttpService.openMyJobsStream({
      onMessage: (res) => this.ngZone.run(() => this.applyJobsResponse(res)),
      onError: () => this.ngZone.run(() => this.startPollingFallback()),
      onClose: () => this.ngZone.run(() => {
        this.jobsStreamAbort = null;
      }),
    });
  }

  private startPollingFallback() {
    if (this.usingPollingFallback || !this.shouldMonitorJobs()) return;

    this.usingPollingFallback = true;
    if (this.jobsStreamAbort) {
      this.jobsStreamAbort.abort();
      this.jobsStreamAbort = null;
    }

    if (this.showJobsDialog) {
      this.fetchJobs(true);
    }

    this.pollingInterval = setInterval(() => {
      if (this.shouldMonitorJobs()) {
        this.fetchJobs(false);
      } else {
        this.clearPolling();
      }
    }, this.POLL_INTERVAL_MS);
  }

  private clearPolling() {
    if (this.pollingInterval !== null) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.usingPollingFallback = false;
  }

  private clearJobsStream(clearPolling = true) {
    if (this.jobsStreamAbort) {
      this.jobsStreamAbort.abort();
      this.jobsStreamAbort = null;
    }
    if (clearPolling) {
      this.clearPolling();
    }
  }

  private formatEta(seconds: number): string {
    if (seconds < 60) return `~${Math.ceil(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return secs > 0 ? `~${mins}m ${secs}s` : `~${mins}m`;
  }

  constructor(
    private reportSearchService: ReportSearchService,
    private _commonHttpService: CommonHttpService,
    private _commonService: CommonService,
    private _authenticationService: AuthenticationService,
    private router: Router,
    private elementRef: ElementRef,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    if (this.isOnAuthorizationRoute()) {
      this._authenticationService.clearAuthTokenReady();
    }

    this.exportJobQueuedSub = this._commonHttpService.exportJobQueued$.subscribe(() => {
      const startingNewSession = !this.exportToastActivated ||
        this.exportToastDismissed ||
        this.exportToastAwaitUserDismiss;

      this.activateExportToast(startingNewSession);
    });

    this.authSub = this._authenticationService.authTokenReady$.subscribe((ready) => {
      if (ready) {
        this.onAuthTokenReady();
      } else {
        this.myJobsFetchedAfterAuth = false;
        this.clearJobsStream();
      }
    });

    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isOnAuthorizationRoute()) {
          this._authenticationService.clearAuthTokenReady();
          this.clearJobsStream();
          return;
        }

        this.syncJobsForCurrentRoute();
      });

    if (this.isOnAuthorizationRoute()) {
      this._authenticationService.clearAuthTokenReady();
    } else {
      this._authenticationService.restoreAuthFromStorage();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showSearchResults = false;
      this.searchKeyword = '';
    }
  }

  /**
   * Load all reports grouped by parent module name
   */
  private loadAllReportsGroupedByParent() {
    this._commonHttpService.getReportSearchData({}).subscribe(res => {
      if (res.status === 1) {
        this.reportsData.modules = res?.data?.modules;
        this.filteredItems = this.reportSearchService.getAllReportsGroupedByParent(this.reportsData);
      }
    });
  }

  /**
   * Search for reports by name
   * @param event - The search event containing the query
   */
  search(event: any) {
    // console.log("Searching for:", event.query);
    this.searchKeyword = event.query;
    
    // If no search query, clear the results
    if (!event.query || !event.query.trim()) {
      this.filteredItems = [];
      this.showSearchResults = false;
      return;
    }

    this.showSearchResults = true;

    this._commonHttpService.getReportSearchData({searchKeyword:event.query}).subscribe(res => {
      // console.log("Found reports:", res);
      if (res.status === 1) {
        this.reportsData.modules = res?.data?.modules;
        
        // Display exact data from API without internal search filtering
        // Group saved reports by parent report name (module name)
        this.filteredItems = this.reportSearchService.getAllReportsGroupedByParent(this.reportsData);
      }
    }); 

    
    // console.log("Found reports:", this.filteredItems);
  }

  /**
   * Handle autocomplete selection
   * @param event - The selection event
   */
  onSelectItem(event: any) {
    // console.log("Autocomplete selection:", event);
    // The event.value will contain the selected item
    if (event.value) {
      this.selectItem(event, event.value);
    }
  }

  /**
   * Search with exact match
   * @param searchTerm - The exact search term
   */
  searchExact(searchTerm: string) {
    if (!searchTerm || !searchTerm.trim()) {
      this.filteredItems = [];
      return;
    }

    this.filteredItems = this.reportSearchService.searchReportsByExactName(
      this.reportsData, 
      searchTerm
    );
  }

  /**
   * Search with module information included
   * @param searchTerm - The search term
   */
  searchWithModuleInfo(searchTerm: string) {
    if (!searchTerm || !searchTerm.trim()) {
      this.filteredItems = [];
      return;
    }

    const results = this.reportSearchService.searchReportsWithModuleInfo(
      this.reportsData, 
      searchTerm
    );
    
    // console.log("Reports with module info:", results);
    // Convert back to SavedReport array for compatibility
    this.filteredItems = results.map(result => {
      const { moduleName, moduleId, ...report } = result;
      return report;
    });
  }

  /**
   * Get all reports from a specific module
   * @param moduleId - The module ID
   */
  getReportsFromModule(moduleId: number) {
    this.filteredItems = this.reportSearchService.getReportsByModuleId(
      this.reportsData, 
      moduleId
    );
  }

  /**
   * Get all unique report names
   */
  getAllReportNames(): string[] {
    return this.reportSearchService.getAllReportNames(this.reportsData);
  }

  /**
   * Get all module names
   */
  getModuleNames(): string[] {
    return this.reportSearchService.getModuleNames(this.reportsData);
  }

  /**
   * Get total count of all reports
   */
  getTotalReportsCount(): number {
    let total = 0;
    if (this.reportsData?.modules) {
      this.reportsData.modules.forEach(module => {
        if (module.savedReports) {
          total += module.savedReports.length;
        }
      });
    }
    return total;
  }

  /**
   * Load reports data from API
   */
  private loadReportsData() {
    // This should be replaced with your actual API call
    // For now, using the sample data structure
    this._commonHttpService.getReports.subscribe(getReports => {
      this.reportsData.modules = getReports;
    });
  }

  /**
   * Example of how to use the search functionality
   */
  /* exampleUsage() {
    // Search for reports containing "Test"
    const testReports = this.reportSearchService.searchReportsByName(this.reportsData, "Test");
    console.log("Reports containing 'Test':", testReports);

    // Search for reports with exact name "Dimension Report Test"
    const exactReports = this.reportSearchService.searchReportsByExactName(this.reportsData, "Dimension Report Test");
    console.log("Exact match reports:", exactReports);

    // Get all reports from module ID 1
    const moduleReports = this.reportSearchService.getReportsByModuleId(this.reportsData, 1);
    console.log("Reports from module 1:", moduleReports);

    // Get all unique report names
    const allReportNames = this.reportSearchService.getAllReportNames(this.reportsData);
    console.log("All report names:", allReportNames);
  } */

  selectItem(event: Event, item: any, module?: any) {
    event?.stopPropagation?.();
    event?.preventDefault?.();

    const [moduleId, reportId] = getReportRouteParams(item, module);

    this.searchKeyword = '';
    this.selectedItem = item;
    this.showSearchResults = false;

    this.router.navigate(['/report-details', moduleId, reportId]);
  }
  searchKeywordClear() {
    this.searchKeyword = '';
    this.showSearchResults = false;
  } 
  private tour = inject(Tourservice); //Take a Tour
  
  startTakeTour(){ 
    // 1. Register the steps
    this.tour.registerSteps([
       {
        el: '#tour-all-vendors',
        t: 'Company Reports',
        d: "Your personalized reporting hub. Reports you've selected from the Reports Library live here. Organized, accessible, and ready to run whenever you need them."
      },
      {
        el: '#Add-data',
        t: 'Reports Library',
        d: 'Explore the full catalog of reports available across your platform. Browse, preview, and add any report to your Company Reports tab with a single click. Tailored to your business needs'
      },
      {
        el: '#actions',
        t: 'Scheduled Reports',
        d: "Never pull a report manually again. View and manage all your scheduled reports here. Track what's running, how often, and where the results are being delivered"
      },
      {
        el: '#dropDownL',
        t: 'Quick Actions at Your Fingertips',
        d: "Click the ⋮ menu on any report to instantly share it with your team, schedule automated delivery, or remove it from your view. All without leaving the page"
      },
      {
        el: '#createReport',
        t: 'Create Your Own Report', // Fixed duplicate title
        d: "Build a custom report from scratch. Choose your data points, apply filters, and visualize it the way that works best for you. Your report saves directly to your Company Reports tab, ready to share or schedule anytime"
      }
    ]);
    this.tour.startTour();
  }


  showDownloads() {
    this.showJobsDialog = true;
    this.selectedStatusFilter = '';

    if (this.jobs.length === 0) {
      this.prevSnapshot = {};
      this.jobsProgressRate = {};
    }

    this.isFetching = false;
    this.syncJobsMonitoring(true);
  }
}
